import type { RouteProtection } from '../types/rbac';
import { ROUTE_PATHS } from '../constants/rbac';

/**
 * Pre-configured route protection patterns for common use cases
 */
export const PROTECTION_PRESETS = {
    /**
     * Public routes - no authentication required
     */
    PUBLIC: {} as RouteProtection,

    /**
     * Authenticated routes - requires login
     */
    AUTHENTICATED: {
        requireAuth: true,
        fallbackPath: ROUTE_PATHS.LOGIN,
    } as RouteProtection,

    /**
     * Admin only routes
     */
    ADMIN_ONLY: {
        requireAuth: true,
        requiredRoles: ['admin'],
        fallbackPath: ROUTE_PATHS.ACCESS_DENIED,
    } as RouteProtection,

    /**
     * Editor or Admin routes
     */
    EDITOR_OR_ADMIN: {
        requireAuth: true,
        requiredRoles: ['editor', 'admin'],
        requireAllRoles: false,
        fallbackPath: ROUTE_PATHS.ACCESS_DENIED,
    } as RouteProtection,

    /**
     * User management routes
     */
    USER_MANAGEMENT: {
        requireAuth: true,
        requiredPermissions: [
            { resource: 'user', action: 'list' },
        ],
        fallbackPath: ROUTE_PATHS.ACCESS_DENIED,
    } as RouteProtection,

    /**
     * User creation routes
     */
    USER_CREATE: {
        requireAuth: true,
        requiredPermissions: [
            { resource: 'user', action: 'create' },
        ],
        fallbackPath: ROUTE_PATHS.ACCESS_DENIED,
    } as RouteProtection,

    /**
     * User role management routes
     */
    USER_ROLE_MANAGEMENT: {
        requireAuth: true,
        requiredPermissions: [
            { resource: 'user', action: 'set-role' },
        ],
        fallbackPath: ROUTE_PATHS.ACCESS_DENIED,
    } as RouteProtection,

    /**
     * Project viewing routes
     */
    PROJECT_VIEW: {
        requireAuth: true,
        requiredPermissions: [
            { resource: 'project', action: 'view' },
        ],
        fallbackPath: ROUTE_PATHS.ACCESS_DENIED,
    } as RouteProtection,

    /**
     * Project creation routes
     */
    PROJECT_CREATE: {
        requireAuth: true,
        requiredPermissions: [
            { resource: 'project', action: 'create' },
        ],
        fallbackPath: ROUTE_PATHS.ACCESS_DENIED,
    } as RouteProtection,

    /**
     * Project editing routes
     */
    PROJECT_EDIT: {
        requireAuth: true,
        requiredPermissions: [
            { resource: 'project', action: 'update' },
        ],
        fallbackPath: ROUTE_PATHS.ACCESS_DENIED,
    } as RouteProtection,

    /**
     * Project deletion routes
     */
    PROJECT_DELETE: {
        requireAuth: true,
        requiredPermissions: [
            { resource: 'project', action: 'delete' },
        ],
        fallbackPath: ROUTE_PATHS.ACCESS_DENIED,
    } as RouteProtection,

    /**
     * Project management routes (create, edit, delete)
     */
    PROJECT_MANAGEMENT: {
        requireAuth: true,
        requiredPermissions: [
            { resource: 'project', action: 'create' },
            { resource: 'project', action: 'update' },
            { resource: 'project', action: 'delete' },
        ],
        requireAllPermissions: false, // User needs ANY of these permissions
        fallbackPath: ROUTE_PATHS.ACCESS_DENIED,
    } as RouteProtection,
} as const;

/**
 * Helper function to create custom protection with common patterns
 */
export const createProtection = {
    /**
     * Require authentication only
     */
    authenticated: (redirectTo?: string): RouteProtection => ({
        requireAuth: true,
        fallbackPath: redirectTo || ROUTE_PATHS.LOGIN,
    }),

    /**
     * Require specific roles (any of the roles)
     */
    anyRole: (roles: string[], redirectTo?: string): RouteProtection => ({
        requireAuth: true,
        requiredRoles: roles as any,
        requireAllRoles: false,
        fallbackPath: redirectTo || ROUTE_PATHS.ACCESS_DENIED,
    }),

    /**
     * Require all specified roles
     */
    allRoles: (roles: string[], redirectTo?: string): RouteProtection => ({
        requireAuth: true,
        requiredRoles: roles as any,
        requireAllRoles: true,
        fallbackPath: redirectTo || ROUTE_PATHS.ACCESS_DENIED,
    }),

    /**
     * Require specific permissions (any of the permissions)
     */
    anyPermission: (permissions: Array<{ resource: string; action: string }>, redirectTo?: string): RouteProtection => ({
        requireAuth: true,
        requiredPermissions: permissions as any,
        requireAllPermissions: false,
        fallbackPath: redirectTo || ROUTE_PATHS.ACCESS_DENIED,
    }),

    /**
     * Require all specified permissions
     */
    allPermissions: (permissions: Array<{ resource: string; action: string }>, redirectTo?: string): RouteProtection => ({
        requireAuth: true,
        requiredPermissions: permissions as any,
        requireAllPermissions: true,
        fallbackPath: redirectTo || ROUTE_PATHS.ACCESS_DENIED,
    }),

    /**
     * Combine role and permission requirements
     */
    roleAndPermission: (
        roles: string[],
        permissions: Array<{ resource: string; action: string }>,
        options?: {
            requireAllRoles?: boolean;
            requireAllPermissions?: boolean;
            redirectTo?: string;
        }
    ): RouteProtection => ({
        requireAuth: true,
        requiredRoles: roles as any,
        requiredPermissions: permissions as any,
        requireAllRoles: options?.requireAllRoles ?? false,
        requireAllPermissions: options?.requireAllPermissions ?? false,
        fallbackPath: options?.redirectTo || ROUTE_PATHS.ACCESS_DENIED,
    }),

    /**
     * Admin only access
     */
    adminOnly: (redirectTo?: string): RouteProtection => ({
        requireAuth: true,
        requiredRoles: ['admin'],
        fallbackPath: redirectTo || ROUTE_PATHS.ACCESS_DENIED,
    }),

    /**
     * Editor or higher access (editor or admin)
     */
    editorOrHigher: (redirectTo?: string): RouteProtection => ({
        requireAuth: true,
        requiredRoles: ['editor', 'admin'],
        requireAllRoles: false,
        fallbackPath: redirectTo || ROUTE_PATHS.ACCESS_DENIED,
    }),

    /**
     * User or higher access (any authenticated user)
     */
    userOrHigher: (redirectTo?: string): RouteProtection => ({
        requireAuth: true,
        requiredRoles: ['user', 'editor', 'admin'],
        requireAllRoles: false,
        fallbackPath: redirectTo || ROUTE_PATHS.ACCESS_DENIED,
    }),
};

/**
 * Route protection for different sections of the application
 */
export const SECTION_PROTECTION = {
    // Dashboard sections
    DASHBOARD: PROTECTION_PRESETS.AUTHENTICATED,

    // Admin sections
    ADMIN_PANEL: PROTECTION_PRESETS.ADMIN_ONLY,
    USER_ADMIN: PROTECTION_PRESETS.USER_MANAGEMENT,
    ROLE_ADMIN: PROTECTION_PRESETS.USER_ROLE_MANAGEMENT,

    // Project sections
    PROJECTS: PROTECTION_PRESETS.PROJECT_VIEW,
    PROJECT_EDITOR: PROTECTION_PRESETS.PROJECT_EDIT,
    PROJECT_CREATOR: PROTECTION_PRESETS.PROJECT_CREATE,

    // Settings sections
    SETTINGS: PROTECTION_PRESETS.AUTHENTICATED,
    SYSTEM_SETTINGS: PROTECTION_PRESETS.ADMIN_ONLY,
} as const;